
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import SavedSearches from '@/components/search/SavedSearches';
import RoommateMatching from '@/components/roommate/RoommateMatching';
import PropertyInsights from '@/components/analytics/PropertyInsights';
import VisitScheduling from '@/components/visits/VisitScheduling';
import ReviewSystem from '@/components/reviews/ReviewSystem';
import EnhancedMessaging from '@/components/messaging/EnhancedMessaging';
import { Search, Users, BarChart3, CalendarDays, Star, MessageCircle, Menu } from 'lucide-react';
import { SEOEnhanced } from '@/components/SEOEnhanced';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Features = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('searches');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const features = [
    {
      id: 'searches',
      label: t('searches', 'البحث', 'Searches'),
      icon: Search,
      component: <SavedSearches />
    },
    {
      id: 'roommates',
      label: t('roommates', 'الرفقاء', 'Roommates'),
      icon: Users,
      component: <RoommateMatching />
    },
    {
      id: 'analytics',
      label: t('insights', 'الإحصائيات', 'Insights'),
      icon: BarChart3,
      component: <PropertyInsights />
    },
    {
      id: 'visits',
      label: t('visits', 'الزيارات', 'Visits'),
      icon: CalendarDays,
      component: <VisitScheduling />
    },
    {
      id: 'reviews',
      label: t('reviews', 'التقييمات', 'Reviews'),
      icon: Star,
      component: (
        <Card>
          <CardHeader>
            <CardTitle>{t('review_demo', 'عرض توضيحي للتقييمات', 'Review System Demo')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ReviewSystem 
              listingId="demo-listing-id" 
              canReview={!!user} 
            />
          </CardContent>
        </Card>
      )
    },
    {
      id: 'messaging',
      label: t('messages', 'الرسائل', 'Messages'),
      icon: MessageCircle,
      component: <EnhancedMessaging />
    }
  ];

  const currentFeature = features.find(f => f.id === activeTab);

  const handleFeatureSelect = (featureId: string) => {
    setActiveTab(featureId);
    setDrawerOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOEnhanced
        title={language === 'ar' 
          ? 'ميزات مسكن المتقدمة - بحث ذكي وأدوات متطورة'
          : 'Maskan Advanced Features - Smart Search & Advanced Tools'
        }
        description={language === 'ar'
          ? 'اكتشف الميزات المتقدمة في مسكن: البحث المحفوظ، مطابقة الرفقاء، إحصائيات العقارات، جدولة الزيارات والمزيد'
          : 'Discover Maskan\'s advanced features: saved searches, roommate matching, property insights, visit scheduling and more'
        }
        keywords="advanced features, saved searches, roommate matching, property insights, visit scheduling, smart search, student housing tools"
        canonical="/features"
        lang={language}
        breadcrumbs={[
          { name: language === 'ar' ? 'الرئيسية' : 'Home', url: 'https://maskan-student-nest.lovable.app/' },
          { name: language === 'ar' ? 'الميزات' : 'Features', url: 'https://maskan-student-nest.lovable.app/features' }
        ]}
      />
      
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-4">
            {t('enhanced_features', 'الميزات المحسنة', 'Enhanced Features')}
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-base">
            {t('features_description', 
              'اكتشف الميزات المتقدمة التي تجعل البحث عن السكن أسهل وأكثر ذكاءً',
              'Discover advanced features that make finding housing easier and smarter'
            )}
          </p>
        </div>

        {isMobile ? (
          // Mobile View with Drawer
          <div className="space-y-6">
            {/* Mobile Feature Selector */}
            <div className="flex items-center justify-between bg-card rounded-lg p-4 border">
              <div className="flex items-center gap-3">
                {currentFeature && (
                  <>
                    <currentFeature.icon className="h-5 w-5 text-primary" />
                    <span className="font-medium">{currentFeature.label}</span>
                  </>
                )}
              </div>
              
              <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                <DrawerTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Menu className="h-4 w-4 mr-2" />
                    {t('switch_feature', 'تغيير الميزة', 'Switch Feature')}
                  </Button>
                </DrawerTrigger>
                <DrawerContent>
                  <DrawerHeader>
                    <DrawerTitle>{t('select_feature', 'اختر ميزة', 'Select Feature')}</DrawerTitle>
                    <DrawerDescription>
                      {t('choose_feature_explore', 'اختر ميزة لتستكشفها', 'Choose a feature to explore')}
                    </DrawerDescription>
                  </DrawerHeader>
                  <div className="p-4 space-y-2">
                    {features.map((feature) => (
                      <Button
                        key={feature.id}
                        variant={activeTab === feature.id ? "default" : "ghost"}
                        className="w-full justify-start gap-3 h-12"
                        onClick={() => handleFeatureSelect(feature.id)}
                      >
                        <feature.icon className="h-5 w-5" />
                        {feature.label}
                      </Button>
                    ))}
                  </div>
                </DrawerContent>
              </Drawer>
            </div>

            {/* Mobile Content */}
            <div className="min-h-[400px]">
              {currentFeature?.component}
            </div>
          </div>
        ) : (
          // Desktop View with Tabs
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-6">
              {features.map((feature) => (
                <TabsTrigger key={feature.id} value={feature.id} className="flex items-center gap-2">
                  <feature.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{feature.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="mt-8">
              {features.map((feature) => (
                <TabsContent key={feature.id} value={feature.id}>
                  {feature.component}
                </TabsContent>
              ))}
            </div>
          </Tabs>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default Features;
